import { AxiosError } from "axios";
import { axiosInstance as axios } from "../axios-provider";
import { AbortRequestEventBus, ModelEventBus, ProgressBarEventBus } from "../../../event-bus";
import { ModelData } from "./ModelData";
import { ErrorHandler } from "../error-handler";
import { editorStateStore } from "../storage/editor-state-store";
import { ImageExporter } from "../transformers/image-exporter";
import { RappidContext } from "../../../../index";
import { curriedGet, curriedPost } from "../abort-controller";
import { DirtyState } from "../dirty-state";
import { waitUntilCleanState } from "../storage/utils";
import { HttpHeaders } from "../../../../utils/http-utils";

export default interface IStatechartsService {
  getModelIdFromPlayer(directoryName: string): Promise<string>;

  getModels(abortable?: boolean, waitForDirtyState?: boolean): Promise<ModelData[]>;

  getModel(id: string, abortable?: boolean): Promise<string>;

  getModelMetadata(id: string, abortable?: boolean): Promise<ModelData>;

  getAsyncModel(id: string, abortable: boolean, showError: boolean): Promise<string>;

  deleteModel(id: string): Promise<boolean>;

  duplicateModel(id: string, newTitle: string): Promise<string>;

  createModel(payload: string, showError?: boolean): Promise<string>;

  updateModel(payload: string, id: string): Promise<string>;

  createOrUpdate(modelId: string, model: string, context: RappidContext): Promise<void>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatModelData(rawData: any): ModelData;

  updateInfo(id: string, description: string, title: string): Promise<boolean | string>;
}

interface StatechartsServiceConfig {
  baseURL: string;
  headers: HttpHeaders;
  params?: { type: "example" | "storage" };
}

export class StatechartsService implements IStatechartsService {
  private readonly config: StatechartsServiceConfig;
  private readonly duplicateConfig: StatechartsServiceConfig;
  private readonly infoConfig: StatechartsServiceConfig;
  private readonly svgConfig: StatechartsServiceConfig;

  constructor(
    private getUrl: string | null,
    private baseURL: string,
    private type: "example" | "storage",
    private dirtyState: DirtyState
  ) {
    this.config = {
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "text/plain",
        Accept: "application/json",
      },
      params: { type: this.type },
    };

    this.duplicateConfig = {
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "text/plain",
        Accept: "application/json",
      },
    };

    this.infoConfig = {
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      params: { type: this.type },
    };

    this.svgConfig = {
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "text/plain",
        Accept: "text/plain",
      },
      params: { type: this.type },
    };
  }

  private getAsyncModelLoopDelay = 0;
  private getAsyncModelTimeout: NodeJS.Timeout;
  private getAsyncModelLoopTimeout: NodeJS.Timeout;
  private abortGetAsyncModelHandler: () => void;

  errorHandler = new ErrorHandler("Statechart service");

  async getModelIdFromPlayer(id: string): Promise<string> {
    if (!id) {
      this.handleError(undefined, "The player id is not valid. New model id couldn't be loaded.");
      return "";
    }
    const modelRequest = "/api/players/" + id + "/open";
    return await axios
      .get(modelRequest, this.config)

      .then((res) => {
        if (!res.data) {
          throw this.handleError(null, "New model id couldn't be loaded.");
        }
        return res.data;
      })
      .catch((res: AxiosError) => {
        throw this.handleError(res, "New model id couldn't be loaded.");
      });
  }

  async getModels(abortable = true, waitForDirtyState = true) {
    if (waitForDirtyState && this.dirtyState?.isDirty()) await waitUntilCleanState(this.dirtyState);

    const requestPromise = curriedGet(this.getUrl, this.config, abortable);
    return await requestPromise
      .then((res) => {
        let data: ModelData[] = [];
        for (const el of res.data) {
          const newEl = this.formatModelData(el);

          data.push(newEl);
        }
        if (data.length < 1) {
          return [];
        }

        data = data.sort((a: ModelData, b: ModelData) => {
          return this.getTime(b.modifiedAt) - this.getTime(a.modifiedAt);
        });

        return data;
      })
      .catch((res: AxiosError) => {
        this.handleError(res, "The statecharts couldn't be loaded.");
        throw res;
      });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatModelData(rawData: any) {
    const newEl: ModelData = rawData;
    // convert only to Date, if value is truthy
    if (rawData.modifiedAt) newEl.modifiedAt = new Date(rawData.modifiedAt);
    if (rawData.createdAt) newEl.createdAt = new Date(rawData.createdAt);
    if (rawData.players?.length > 0) {
      for (const index in rawData.players) {
        if (rawData.players[index].creationDate)
          newEl.players[index].creationDate = new Date(rawData.players[index].creationDate);
      }
    }
    if (rawData.players) {
      newEl.players = JSON.parse(rawData.players);
      if (newEl.players?.length > 0) {
        for (const index in newEl.players) {
          if (newEl.players[index].creationDate)
            newEl.players[index].creationDate = new Date(newEl.players[index].creationDate);
        }
      }
    }
    if (!rawData.svg || rawData.svg?.toLowerCase().includes('width="0" height="0"')) {
      newEl.svgUrl = null;
    } else {
      const blob = new Blob([rawData.svg], { type: "image/svg+xml" });
      newEl.svgUrl = URL.createObjectURL(blob);
    }
    return newEl;
  }

  private getTime(date?: Date): number {
    return date !== null ? date.getTime() : 0;
  }

  async duplicateModel(id: string, newTitle: string): Promise<string> {
    if (!id) {
      throw this.handleError(undefined, "The model id is not valid. Model couldn't be loaded.");
    }
    const modelRequestUrl = this.getUrl + "/" + id + "/duplicate?newName=" + newTitle;
    return await axios
      .get(modelRequestUrl, this.duplicateConfig)
      .then((res) => {
        if (res.status !== 200) {
          throw this.handleError(null, "Model couldn't be duplicated.");
        }
        return res.data;
      })
      .catch((res: AxiosError) => {
        throw this.handleError(res, "Model couldn't be duplicated.");
      });
  }

  async getAsyncModel(id: string, abortable = true, showError = true) {
    if (!id) {
      throw this.handleError(null, "Import failed. The model id is not valid.");
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: (id: string) => void, reject: (reason?: any) => void) => {
      this.abortGetAsyncModelHandler = () => {
        this.abortGetAsyncModel(id, reject, "Import got manually aborted.");
      };
      AbortRequestEventBus.on("abort", this.abortGetAsyncModelHandler);

      // start immediately with the first request
      this.getAsyncModelLoopDelay = 0;
      this.getAsyncModelLoop(resolve, reject, id, abortable, showError);

      this.getAsyncModelTimeout = setTimeout(
        () => {
          this.abortGetAsyncModel(id, reject, "Import failed, as it timed out.");
        },
        1000 * 60 * 3
      );
    });
  }

  private abortGetAsyncModel(
    id: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reject: (reason?: any) => void,
    errMsg = "Import failed. Possibly because the format of the content is invalid."
  ) {
    this.cleanUpGetAsyncModel();
    reject(errMsg);
    AbortRequestEventBus.emit("abort");
    this.deleteModel(id, false);
  }

  private cleanUpGetAsyncModel() {
    AbortRequestEventBus.off("abort", this.abortGetAsyncModelHandler);
    if (this.getAsyncModelLoopTimeout) clearTimeout(this.getAsyncModelLoopTimeout);
    if (this.getAsyncModelTimeout) clearTimeout(this.getAsyncModelTimeout);
  }

  private getAsyncModelLoop(
    resolve: (id: string) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reject: (reason?: any) => void,
    id: string,
    abortable = true,
    showError = true
  ) {
    this.getAsyncModelLoopTimeout = setTimeout(async () => {
      await this.getModel(id, abortable, false)
        .then((res) => {
          this.cleanUpGetAsyncModel();
          resolve(res);
        })
        .catch((res: AxiosError) => {
          if (res.response.status === 500) {
            this.abortGetAsyncModel(id, reject);
            return;
          }
          // start with the next request only after 1s
          this.getAsyncModelLoopDelay = 1000;
          this.getAsyncModelLoop(resolve, reject, id, abortable, showError);
        });
    }, this.getAsyncModelLoopDelay);
  }

  async getModel(id: string, abortable = true, showError = true): Promise<string> {
    if (!id) {
      throw this.handleError(null, "The model id is not valid. Model couldn't be loaded.");
    }
    const modelRequest = this.getUrl + "/" + id;
    const requestPromise = curriedGet(modelRequest, this.config, abortable);
    const errMsg = "Model couldn't be loaded.";
    return await requestPromise

      .then((res) => {
        if (!res.data) {
          throw this.handleError(null, errMsg, showError);
        }
        editorStateStore().setModelId(id);

        return res.data;
      })
      .catch((res: AxiosError) => {
        if (showError) {
          this.handleError(res, errMsg);
        }
        throw res;
      });
  }

  private getModelMetadataUrl(modelId: string) {
    return `/api/models/${modelId}/metadata`;
  }

  async getModelMetadata(modelId: string, waitForDirtyState = true, abortable = true): Promise<ModelData> {
    if (waitForDirtyState && this.dirtyState?.isDirty()) await waitUntilCleanState(this.dirtyState);

    const requestPromise = curriedGet(this.getModelMetadataUrl(modelId), this.config, abortable);

    return await requestPromise.then((res) => {
      let newEl: ModelData = res.data;
      if (newEl) {
        newEl = this.formatModelData(res.data);
      } else {
        throw "Format of received data invalid";
      }
      return newEl;
    });
  }

  async createOrUpdate(modelId: string, model: string, context: RappidContext) {
    if (!modelId) {
      modelId = await this.createModel(model).then((id: string) => {
        editorStateStore().setModelId(id);
        ModelEventBus.emit("modelIdCreated");
        this.saveSvg(id, context);
        return id;
      });
    } else {
      await this.updateModel(model, modelId).then(() => {
        this.saveSvg(modelId, context);
      });
    }
  }

  async deleteModel(id: string, showError = true): Promise<boolean> {
    // delete only for cloudStatechartService but not for exampleStatechartService
    if (this.type === "storage") {
      return await axios
        .delete(this.getUrl + "/" + id, this.config)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (res.status !== 200) {
            throw this.handleError(res, "Model couldn't be deleted.", showError);
          }
          return true;
        })
        .catch((res: AxiosError) => {
          throw this.handleError(res, "Model couldn't be deleted.", showError);
        });
    } else {
      throw this.handleError(null, "Delete invalid.", showError);
    }
  }
  async createModel(payload: string, showError = true): Promise<string> {
    // post only for cloudStatechartService but not for exampleStatechartService
    if (this.type === "storage") {
      const requestPromise = curriedPost(this.getUrl, this.config, true, payload);

      return await requestPromise
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (!res.data) {
            throw this.handleError(null, "Model couldn't be created.", showError);
          }
          return res.data;
        })
        .catch((res: AxiosError) => {
          throw this.handleError(null, "Model couldn't be created.", showError);
        });
    } else {
      throw this.handleError(null, "Post invalid.", showError);
    }
  }

  async updateInfo(id: string, description: string, title: string): Promise<boolean | string> {
    const payload = { description: description, title: title };
    // post only for cloudStatechartService but not for exampleStatechartService
    if (this.type === "storage") {
      const url = `${this.getUrl}/${id}/info`;
      return await axios
        .post(url, payload, this.infoConfig)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (res.status !== 200) {
            throw res;
          }
          return true;
        })
        .catch((res: AxiosError) => {
          throw this.handleError(res, "Info couldn't be posted.");
        });
    } else {
      throw this.handleError(null, "Post invalid.");
    }
  }

  private async saveSvg(id: string, context: RappidContext) {
    new ImageExporter().exportSvg(context.paper, context.paperScroller, "", async (svg: string) => {
      const url = this.getUrl + "/" + id + "/svg";
      return await axios.post(url, svg, this.svgConfig);
    });
  }

  async updateModel(payload: string, id: string): Promise<string> {
    // post only for cloudStatechartService but not for exampleStatechartService
    if (this.type === "storage") {
      const requestPromise = curriedPost(this.getUrl + "/" + id, this.config, true, payload);

      return await requestPromise
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (!res.data) {
            throw this.handleError(res, "Model couldn't be updated.");
          }
          return res.data;
        })
        .catch((res: AxiosError) => {
          throw this.handleError(res, "Model couldn't be updated.");
        });
    } else {
      throw this.handleError(null, "Update invalid.");
    }
  }

  protected handleError(res: AxiosError | null, text: string, showError = true) {
    ProgressBarEventBus.emit("finishedTask");
    return this.errorHandler.handle(res, "statecharts-service", text, showError);
  }
}
